/**
 * ArtefactDetector Utility
 *
 * Orchestrates automatic detection and extraction of artefacts from AI-generated message content.
 * Delegates to specialized detectors for code blocks, documents, tables, charts, and HTML content.
 */

import {
  Artefact,
  ArtefactType,
  ArtefactDetectionConfig,
  ArtefactDetectionResult,
  DEFAULT_DETECTION_CONFIG,
} from "@/types/artefacts";

import { CodeDetector, DocumentDetector, TableDetector, HTMLDetector } from "./ArtefactDetectors";

// Initialize detector instances
const codeDetector = new CodeDetector();
const documentDetector = new DocumentDetector();
const tableDetector = new TableDetector();
const htmlDetector = new HTMLDetector();

/**
 * Main artefact detection function - orchestrates specialized detectors
 */
export function detectArtefacts(
  content: string,
  messageId: string,
  config: ArtefactDetectionConfig = DEFAULT_DETECTION_CONFIG,
): ArtefactDetectionResult {
  const artefacts: Artefact[] = [];
  const warnings: string[] = [];
  let skippedCount = 0;

  if (!content || typeof content !== "string") {
    return { artefacts: [], skippedCount: 0, warnings: ["Invalid content provided"] };
  }

  // Delegate to specialized detectors based on configuration
  if (config.enabledTypes.includes(ArtefactType.CODE)) {
    const codeResult = codeDetector.detect(content, messageId, config);

    artefacts.push(...codeResult.artefacts);
    skippedCount += codeResult.skippedCount;
    warnings.push(...codeResult.warnings);
  }

  if (config.enabledTypes.includes(ArtefactType.DOCUMENT)) {
    const docResult = documentDetector.detect(content, messageId, config);

    artefacts.push(...docResult.artefacts);
    skippedCount += docResult.skippedCount;
    warnings.push(...docResult.warnings);
  }

  if (config.enabledTypes.includes(ArtefactType.TABLE)) {
    const tableResult = tableDetector.detect(content, messageId, config);

    artefacts.push(...tableResult.artefacts);
    skippedCount += tableResult.skippedCount;
    warnings.push(...tableResult.warnings);
  }

  if (config.enabledTypes.includes(ArtefactType.HTML)) {
    const htmlResult = htmlDetector.detect(content, messageId, config);

    artefacts.push(...htmlResult.artefacts);
    skippedCount += htmlResult.skippedCount;
    warnings.push(...htmlResult.warnings);
  }

  return { artefacts, skippedCount, warnings };
}

/**
 * Validation helper
 */
export function validateArtefact(artefact: Partial<Artefact>): artefact is Artefact {
  return !!(
    artefact &&
    artefact.id &&
    artefact.type &&
    artefact.title &&
    artefact.content &&
    artefact.messageId &&
    artefact.timestamp
  );
}
